import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { IReqAuthenticateClient, IReqGoogleReview, IReqSendOtp, IReqVerifyOtp } from 'pages/tradeIn/TradeInInterface';
import {
  SendOtpAPI,
  VerifyOtpAPI,
  authenticateClientAPI,
  getTokenAPI,
  getValuationAPI,
  googleReviewAPI,
} from './ApiEndPoints';
import { Review } from 'pages/googleReview/googleReviewInterface';
import { ApiResponse } from './CommonInterface';

const initialState: {
  loading: boolean;
  sendOtpData: { message?: string; success?: boolean };
  verifyOtpData: {
    message?: string | { valid?: boolean };
    success?: boolean;
  };
  googleReviewData: Review[];
  authenticateClientData: ApiResponse;
  token: string;
  blackBookQuestions: [];
  offerValue: any;
} = {
  loading: false,
  sendOtpData: {},
  verifyOtpData: {},
  googleReviewData: [],
  authenticateClientData: {},
  token: '',
  blackBookQuestions: [],
  offerValue: {},
};

export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    console.error(error);

    return error?.response?.data?.message || 'Unknown error';
  } else {
    // Handle non-Axios errors if needed
    console.error('Non-Axios error:', error);
    return 'Unknown error';
  }
};

export const sendOtp = createAsyncThunk('commonModule/sendOtp', async (data: IReqSendOtp) => {
  try {
    const response = await axios.post(`${SendOtpAPI}`, data);

    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
});

export const verifyOtp = createAsyncThunk('commonModule/verifyOtp', async (data: IReqVerifyOtp) => {
  try {
    const response = await axios.post(`${VerifyOtpAPI}`, data);

    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
});

export const getIpv4Data = async () => {
  const retryCount = 0;
  const maxRetries = 2;

  while (retryCount < maxRetries) {
    try {
      const IPv4Res = await axios.get('https://api.ipify.org?format=json');
      // const IPv4Res = await axios.get('https://geolocation-db.com/json/');

      return IPv4Res?.data?.ip;
    } catch (error) {
      // Check if the error is a 500 status code
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }

  // If max retries reached without success, throw an error
  throw new Error('Failed to fetch IPv4 data after multiple retries');
};

export const googleReview = createAsyncThunk('commonModule/googleReview', async (queryParams: IReqGoogleReview) => {
  try {
    const queryString = Object.keys(queryParams)
      .map((key) => `${key}=${queryParams[key]}`)
      .join('&');

    const response = await axios.get(`${googleReviewAPI}?${queryString}`);

    // const response = await axios.get(`${googleReviewAPI}`, { data });

    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
});

export const authenticateClient = createAsyncThunk(
  'commonModule/authenticateClient',
  async (data: IReqAuthenticateClient) => {
    try {
      const response = await axios.post(`${authenticateClientAPI}?token=${data.token}&domain=${data.domain}`, data);

      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
);

export const getToken = createAsyncThunk('commonModule/getToken', async (customerId: string) => {
  try {
    const response = await axios.get(`${getTokenAPI}${customerId}`);

    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
});

export const getBlackBookQuestions = createAsyncThunk('commonModule/getBlackBookQuestions', async (data: object) => {
  try {
    const response = await axios.get(`${getTokenAPI}${data.customerId}`);
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
});

export const postBlackBookAnswers = createAsyncThunk('commonModule/postBlackBookAnswers', async (data: object) => {
  try {
    const response = await axios.post(
      `${getValuationAPI}${data.visitor}?customerId=${data.customerId}&token=${data.token}`,
      data.postData
    );

    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
});

export const commonModuleState = createSlice({
  name: 'commonModule',
  initialState,
  reducers: {
    clearVerifyOtpData: (state) => {
      return {
        ...state,
        verifyOtpData: initialState.verifyOtpData,
      };
    },
    clearSendOtpData: (state) => {
      return {
        ...state,
        sendOtpData: initialState.sendOtpData,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.sendOtpData = action.payload;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.loading = false;
        state.sendOtpData = {
          message: action.error.message,
          success: false,
        };
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.verifyOtpData = action.payload;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.verifyOtpData = {
          message: action.error.message,
          success: false,
        };
      })
      .addCase(authenticateClient.fulfilled, (state, action) => {
        state.loading = false;
        state.authenticateClientData = action.payload;
      })
      .addCase(authenticateClient.rejected, (state, action) => {
        state.loading = false;
        state.authenticateClientData = {
          message: action.error.message,
          success: false,
        };
      })
      .addCase(googleReview.fulfilled, (state, action) => {
        state.loading = false;
        state.googleReviewData = action.payload?.message?.response?.result?.reviews || [];
      })
      .addCase(getToken.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload?.message?.token;
      })
      .addCase(getBlackBookQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.blackBookQuestions = action.payload;
      })
      .addCase(postBlackBookAnswers.fulfilled, (state, action) => {
        state.loading = false;
        state.offerValue = action.payload?.message;
      });
  },
});

export const { clearVerifyOtpData, clearSendOtpData } = commonModuleState.actions;

export default commonModuleState.reducer;
